import React, { useRef, useState, useEffect, useCallback } from "react";

const MultiSelect = (props) => {
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const [filter, setFilter] = useState("");

  const isOpen = props.isHovering;
  const selected = props.selectedOptions || [];

  /* ── click outside → close ── */
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        props.setIsHovering(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  /* ── focus search on open ── */
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  /* ── filtered options ── */
  const filtered = filter.trim()
    ? props.options.filter((o) =>
        o.label.toString().toLowerCase().includes(filter.toLowerCase())
      )
    : props.options;

  /* ── toggle ── */
  const toggle = useCallback(
    (option) => {
      let next;
      if (option.value === "all") {
        // if all selected → deselect all, else select all
        next = selected.includes("all")
          ? []
          : props.options.map((o) => o.value);
      } else {
        next = selected.includes(option.value)
          ? selected.filter((v) => v !== option.value && v !== "all")
          : [...selected, option.value];

        // if all non-"all" are selected, add "all" too
        const nonAll = props.options.filter((o) => o.value !== "all");
        if (nonAll.every((o) => next.includes(o.value))) {
          next = props.options.map((o) => o.value);
        }
      }
      props.setSelectedOptions(next);
      props.onChange(next);
    },
    [selected, props.options, props.setSelectedOptions, props.onChange]
  );

  /* ── display text ── */
  const getLabel = () => {
    if (selected.length === 0) return "Wybierz...";
    if (selected.includes("all")) return "Wszystkie";
    if (selected.length <= 2) {
      return selected
        .filter((v) => v !== "all")
        .map((v) => {
          const opt = props.options.find((o) => o.value === v);
          return opt ? opt.label : v;
        })
        .join(", ");
    }
    return `${selected.filter((v) => v !== "all").length} wybranych`;
  };

  return (
    <div className="ms-wrap" ref={wrapRef} id={props.id}>
      {/* ── Trigger ── */}
      <div
        className={`ms-trigger ${isOpen ? "ms-trigger--open" : ""}`}
        id={props.id}
        onClick={() => props.setIsHovering(!isOpen)}
      >
        <span className="ms-trigger-text" id={props.id}>
          {getLabel()}
        </span>
        <span className="ms-trigger-arrow" id={props.id}>
          {isOpen ? "▲" : "▼"}
        </span>
      </div>

      {/* ── Dropdown ── */}
      {isOpen && (
        <div className="ms-dropdown" id={props.id}>
          <div className="ms-search-row" id={props.id}>
            <input
              ref={inputRef}
              type="text"
              className="ms-search"
              id={props.id}
              placeholder="Szukaj..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            <button
              className="ms-close"
              id={props.id}
              onClick={() => {
                setFilter("");
                props.setIsHovering(false);
              }}
            >
              ✕
            </button>
          </div>

          <div className="ms-options" id={props.id}>
            {filtered.map((option) => {
              const checked = selected.includes(option.value);
              return (
                <label
                  key={option.value}
                  id={props.id}
                  className={`ms-option ${checked ? "ms-option--active" : ""}`}
                >
                  <input
                    type="checkbox"
                    className="ms-checkbox"
                    id={props.id}
                    checked={checked}
                    onChange={() => toggle(option)}
                  />
                  <span className="ms-option-label" id={props.id}>
                    {option.label}
                  </span>
                </label>
              );
            })}
            {filtered.length === 0 && (
              <div className="ms-empty">Brak wyników</div>
            )}
          </div>

          {selected.filter((v) => v !== "all").length > 0 && (
            <div className="ms-footer" id={props.id}>
              <button
                className="ms-clear"
                id={props.id}
                onClick={() => {
                  props.setSelectedOptions([]);
                  props.onChange([]);
                }}
              >
                Wyczyść ({selected.filter((v) => v !== "all").length})
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
